import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Bell, Briefcase, Home, MessageCircle, Search, User, Users } from "lucide-react";
import { toast } from "sonner";
import { api, WS_URL } from "@/lib/api";
import { startQueueWorker } from "@/lib/chatQueue";
import { useAuth } from "@/context/AuthContext";
import { Logo, ThemeToggle } from "@/components/common";

const TABS = [
  { to: "/app", label: "Accueil", icon: Home, end: true, id: "accueil" },
  { to: "/app/reseau", label: "Réseau", icon: Users, id: "reseau" },
  { to: "/app/operations", label: "Opérations", icon: Briefcase, id: "operations" },
  { to: "/app/messages", label: "Messages", icon: MessageCircle, id: "messages" },
  { to: "/app/moi", label: "Moi", icon: User, id: "moi" },
];

function useRealtime(onEvent) {
  useEffect(() => {
    let ws;
    let stop = false;
    let retry;
    const connect = () => {
      ws = new WebSocket(WS_URL);
      ws.onmessage = (e) => {
        const data = JSON.parse(e.data);
        window.dispatchEvent(new CustomEvent("mr-ws", { detail: data }));
        onEvent(data);
      };
      ws.onclose = () => {
        if (!stop) retry = setTimeout(connect, 4000);
      };
    };
    connect();
    window.mrWsSend = (obj) => ws?.readyState === 1 && ws.send(JSON.stringify(obj));
    const ping = setInterval(() => ws?.readyState === 1 && ws.send("ping"), 25000);
    return () => {
      stop = true;
      clearTimeout(retry);
      clearInterval(ping);
      ws?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export default function MemberLayout() {
  const { me } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const wide = pathname === "/app" || pathname === "/app/" || /^\/app\/(reseau|fil)\/[^/]+$/.test(pathname);
  const [unread, setUnread] = useState(0);
  const [offline, setOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setUnread(data.unread)).catch(() => {});
    return startQueueWorker();
  }, []);

  useRealtime((evt) => {
    if (evt.type === "notification") {
      setUnread((u) => u + 1);
      const onThread = evt.link && window.location.pathname === evt.link;
      if (!onThread) toast(evt.title, { description: evt.body, action: evt.link ? { label: "Ouvrir", onClick: () => nav(evt.link) } : undefined });
    }
  });

  return (
    <div className="min-h-screen bg-mr-bg">
      <header className="sticky top-0 z-30 border-b border-mr-border bg-[color-mix(in_srgb,var(--mr-bg)_88%,transparent)] backdrop-blur-xl">
        <div className={`${wide ? "max-w-6xl" : "max-w-2xl"} mx-auto px-4 h-14 flex items-center justify-between`}>
          <span className="sm:hidden"><Logo to="/app" testId="member-mobile-logo" compact /></span>
          <span className="hidden sm:inline-flex"><Logo to="/app" testId="member-logo-link" /></span>
          <div className="flex items-center gap-1.5">
            <button data-testid="member-search-button" aria-label="Rechercher" onClick={() => nav("/app/recherche")} className="grid place-items-center w-11 h-11 text-mr-text2 hover:text-mr-text"><Search size={19} /></button>
            <Link to="/app/notifications" onClick={() => setUnread(0)} data-testid="member-notifications-bell" aria-label="Notifications" className="relative grid place-items-center w-11 h-11 text-mr-text2 hover:text-mr-text">
              <Bell size={19} />
              {unread > 0 && <span data-testid="member-notifications-count" className="absolute top-2 right-2 min-w-[17px] h-[17px] px-1 rounded-full bg-mr-accent text-mr-on-accent text-[10px] font-bold grid place-items-center tabular">{unread > 9 ? "9+" : unread}</span>}
            </Link>
            <ThemeToggle />
          </div>
        </div>
        {offline && (
          <div data-testid="member-offline-banner" className="text-center text-xs py-2 px-4 border-t border-mr-border text-mr-muted">
            Hors connexion : dernières conversations et programmes de voyage en lecture seule.
          </div>
        )}
        {me.user.status === "suspendu" && (
          <div data-testid="member-suspended-banner" className="text-center text-xs py-2 px-4 border-t border-mr-border text-mr-danger">
            Compte suspendu : lecture et paiements uniquement. <Link to="/app/moi?tab=cotisation" className="underline">Renouveler ma cotisation</Link>
          </div>
        )}
      </header>
      <main className={`${wide ? "max-w-6xl" : "max-w-2xl"} mx-auto px-4 pt-6 pb-28`}>
        <Outlet />
      </main>
      <nav className="fixed bottom-0 inset-x-0 z-30 border-t border-mr-border bg-mr-bg pb-safe" data-testid="member-bottom-nav">
        <div className="max-w-2xl mx-auto grid grid-cols-5">
          {TABS.map(({ to, label, icon: Icon, end, id }) => (
            <NavLink key={to} to={to} end={end} data-testid={`member-tab-${id}`}
              className={({ isActive }) => `flex flex-col items-center justify-center gap-1 h-16 text-[11px] font-medium transition-colors ${isActive ? "text-mr-accent" : "text-mr-muted hover:text-mr-text"}`}>
              <Icon size={21} strokeWidth={1.8} />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
